import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const VERDICT_TONES = {
  MATCH: "success",
  PARTIAL: "warn",
  MISSING: "danger",
  SURPLUS: "muted",
};

const VERDICT_LABELS = {
  MATCH: "Match",
  PARTIAL: "Partial",
  MISSING: "Missing",
  SURPLUS: "Surplus",
};

function pct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function Metric({ label, value, tone, testId }) {
  const color =
    tone === "success"
      ? "#1f6c3f"
      : tone === "danger"
        ? "#8a2b0a"
        : tone === "warn"
          ? "#8a5a0a"
          : "var(--bo-ink)";
  return (
    <div style={{ minWidth: 120 }}>
      <div className="bo-eyebrow">{label}</div>
      <div
        style={{ fontSize: 30, color, fontFamily: "var(--bo-serif)" }}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  return (
    <Pill tone={VERDICT_TONES[verdict]} testId={`verdict-${verdict}`}>
      {VERDICT_LABELS[verdict] || verdict}
    </Pill>
  );
}

function ValuePreview({ value }) {
  if (value == null || value === "")
    return <em style={{ color: "var(--bo-muted)" }}>(none)</em>;
  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
        {value.slice(0, 4).map((v, i) => (
          <li key={i}>{String(v)}</li>
        ))}
        {value.length > 4 && (
          <li style={{ color: "var(--bo-muted)" }}>…+{value.length - 4}</li>
        )}
      </ul>
    );
  }
  return <span>{String(value)}</span>;
}

export default function GoldenPath() {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runBusy, setRunBusy] = useState(false);
  const [kickBusy, setKickBusy] = useState(false);
  const [kickResult, setKickResult] = useState(null);
  const [kickError, setKickError] = useState(null);
  const [kickUrl, setKickUrl] = useState("https://precipice-health.com");

  const [runReq, setRunReq] = useState({
    golden_brand_id: "",
    golden_product_id: "",
    golden_audience_id: "",
    since_run_id: "",
    note: "",
  });

  const [orgId, setOrgId] = useState(null);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/genesis/golden-path/reports?limit=25`);
      setReports(r.data.reports || []);
      if (r.data.reports?.[0]) {
        await loadReport(r.data.reports[0].id);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (reportId) => {
    try {
      const r = await api.get(`/genesis/golden-path/reports/${reportId}`);
      setSelected(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const loadMe = async () => {
    try {
      const r = await api.get(`/auth/me`);
      const org = r.data?.memberships?.[0]?.org_id;
      if (org) setOrgId(org);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadMe();
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReport = async () => {
    if (!orgId) {
      setError("Session missing org_id — try refreshing the page.");
      return;
    }
    setRunBusy(true);
    setError(null);
    try {
      const body = {
        golden_org_id: orgId,
        golden_brand_id: runReq.golden_brand_id || null,
        golden_product_id: runReq.golden_product_id || null,
        golden_audience_id: runReq.golden_audience_id || null,
        since_run_id: runReq.since_run_id || null,
        note: runReq.note || null,
      };
      const r = await api.post(`/genesis/golden-path/run`, body);
      setSelected(r.data);
      await loadReports();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRunBusy(false);
    }
  };

  const kickPipeline = async () => {
    setKickBusy(true);
    setKickError(null);
    setKickResult(null);
    try {
      const r = await api.post(`/genesis/golden-path/kickoff`, {
        root_url: kickUrl,
        max_pages: 6,
        max_depth: 2,
      });
      setKickResult(r.data);
    } catch (e) {
      setKickError(formatApiError(e));
    } finally {
      setKickBusy(false);
    }
  };

  const verdictCounts = selected?.counts || {};

  return (
    <Shell>
      <div className="bo-page" data-testid="golden-path-page">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Genesis · M5</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 32,
              color: "var(--bo-ink)",
              margin: "4px 0 8px",
            }}
          >
            Genesis Fixture Golden Path
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 720 }}>
            Blind-tests Genesis by pointing it at a URL as if this were a
            brand-new tenant, then measures the proposals it produces
            against the APPROVED IntelligenceStatements you already trust.
            Match / Partial / Missing / Surplus verdicts roll up into
            precision, recall, and F1.
          </p>
        </div>

        {/* Kickoff row */}
        <div
          className="bo-card"
          style={{ padding: 14, marginBottom: 16 }}
          data-testid="kickoff-panel"
        >
          <div className="bo-eyebrow" style={{ marginBottom: 6 }}>
            1 · Populate the Genesis fixture from a URL
          </div>
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
          >
            <input
              className="bo-input"
              type="url"
              value={kickUrl}
              onChange={(e) => setKickUrl(e.target.value)}
              style={{ flex: 1, minWidth: 320 }}
              placeholder="https://your-brand-site.com"
              data-testid="kickoff-url-input"
            />
            <button
              className="bo-btn bo-btn--primary"
              onClick={kickPipeline}
              disabled={kickBusy || !kickUrl}
              data-testid="kickoff-run-button"
            >
              {kickBusy ? "Queuing pipeline…" : "Run blind pipeline"}
            </button>
          </div>
          {kickResult && (
            <div
              style={{ marginTop: 8, fontSize: 13, color: "var(--bo-muted)" }}
              data-testid="kickoff-result"
            >
              Pipeline job <code>{kickResult.job_id.slice(0, 8)}</code> queued.
              Shadow brand <code>{kickResult.shadow_brand_id.slice(0, 8)}</code>
              , product <code>{kickResult.shadow_product_id.slice(0, 8)}</code>,
              audience <code>{kickResult.shadow_audience_id.slice(0, 8)}</code>.
              Wait ~1 minute for extraction, then run a report below.
            </div>
          )}
          {kickError && <ErrorBanner error={kickError} testId="kickoff-error" />}
        </div>

        {/* Report run row */}
        <div
          className="bo-card"
          style={{ padding: 14, marginBottom: 16 }}
          data-testid="run-report-panel"
        >
          <div className="bo-eyebrow" style={{ marginBottom: 6 }}>
            2 · Score the Genesis fixture against your golden Brains
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <input
              className="bo-input"
              placeholder="Golden brand_id (optional)"
              value={runReq.golden_brand_id}
              onChange={(e) =>
                setRunReq((r) => ({ ...r, golden_brand_id: e.target.value }))
              }
              data-testid="run-brand-id"
            />
            <input
              className="bo-input"
              placeholder="Golden product_id (optional)"
              value={runReq.golden_product_id}
              onChange={(e) =>
                setRunReq((r) => ({ ...r, golden_product_id: e.target.value }))
              }
              data-testid="run-product-id"
            />
            <input
              className="bo-input"
              placeholder="Golden audience_id (optional)"
              value={runReq.golden_audience_id}
              onChange={(e) =>
                setRunReq((r) => ({
                  ...r,
                  golden_audience_id: e.target.value,
                }))
              }
              data-testid="run-audience-id"
            />
          </div>
          <input
            className="bo-input"
            placeholder="Run note (optional)"
            value={runReq.note}
            onChange={(e) =>
              setRunReq((r) => ({ ...r, note: e.target.value }))
            }
            style={{ width: "100%", marginBottom: 8 }}
            data-testid="run-note"
          />
          <button
            className="bo-btn bo-btn--primary"
            onClick={runReport}
            disabled={runBusy || !orgId}
            data-testid="run-report-button"
          >
            {runBusy ? "Scoring…" : "Run accuracy report"}
          </button>
        </div>

        {error && <ErrorBanner error={error} testId="golden-path-error" />}
        {loading && !selected && (
          <LoadingCard label="Loading Golden Path reports…" />
        )}

        {/* Selected report */}
        {selected && (
          <div>
            <div
              className="bo-card"
              style={{
                display: "flex",
                gap: 24,
                padding: 16,
                marginBottom: 16,
                alignItems: "center",
                flexWrap: "wrap",
              }}
              data-testid="selected-report-summary"
            >
              <Metric
                label="Precision"
                value={pct(selected.precision)}
                testId="metric-precision"
              />
              <Metric
                label="Recall"
                value={pct(selected.recall)}
                testId="metric-recall"
              />
              <Metric
                label="F1"
                value={pct(selected.f1)}
                testId="metric-f1"
              />
              <Metric
                label="Match"
                value={verdictCounts.match ?? 0}
                tone="success"
                testId="metric-match"
              />
              <Metric
                label="Partial"
                value={verdictCounts.partial ?? 0}
                tone="warn"
                testId="metric-partial"
              />
              <Metric
                label="Missing"
                value={verdictCounts.missing ?? 0}
                tone="danger"
                testId="metric-missing"
              />
              <Metric
                label="Surplus"
                value={verdictCounts.surplus ?? 0}
                testId="metric-surplus"
              />
            </div>

            <div
              className="bo-card"
              style={{ padding: 12, marginBottom: 12 }}
              data-testid="report-provenance"
            >
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>
                Provenance
              </div>
              <div style={{ fontSize: 13, color: "var(--bo-muted)" }}>
                Report <code>{selected.id.slice(0, 8)}</code>
                {selected.note ? ` · ${selected.note}` : ""} · golden fields{" "}
                {selected.golden_field_total} · fixture proposals{" "}
                {selected.tenant_zero_proposal_total}
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              {selected.per_field.length === 0 ? (
                <EmptyState
                  title="Nothing to compare yet"
                  description="Run the blind pipeline above, wait for extraction, then run the accuracy report."
                  testId="report-empty"
                />
              ) : (
                selected.per_field.map((e) => (
                  <div
                    key={`${e.scope}:${e.field_path}:${e.verdict}`}
                    className="bo-card"
                    style={{ padding: 12, marginBottom: 8 }}
                    data-testid={`per-field-row-${e.field_path}`}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <VerdictBadge verdict={e.verdict} />
                      <Pill tone="muted">{e.scope}</Pill>
                      <span
                        style={{
                          fontFamily: "var(--bo-serif)",
                          fontSize: 16,
                        }}
                      >
                        {e.field_path}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                        marginTop: 10,
                      }}
                    >
                      <div>
                        <div className="bo-eyebrow">Golden value</div>
                        <ValuePreview value={e.golden_value} />
                      </div>
                      <div>
                        <div className="bo-eyebrow">Tenant Zero candidates</div>
                        {(e.tenant_zero_values || []).length === 0 ? (
                          <em style={{ color: "var(--bo-muted)" }}>(none)</em>
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {(e.tenant_zero_values || []).slice(0, 4).map(
                              (v, i) => (
                                <li key={i}>
                                  <ValuePreview value={v} />
                                </li>
                              )
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* History rail */}
        {reports.length > 0 && (
          <div
            className="bo-card"
            style={{ marginTop: 24, padding: 12 }}
            data-testid="report-history"
          >
            <div className="bo-eyebrow" style={{ marginBottom: 6 }}>
              History
            </div>
            {reports.map((r) => (
              <button
                key={r.id}
                className="bo-btn bo-btn--ghost"
                onClick={() => loadReport(r.id)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: 13,
                  textAlign: "left",
                  background:
                    selected?.id === r.id
                      ? "var(--bo-panel-alt, #f6f4ef)"
                      : "transparent",
                }}
                data-testid={`report-history-item-${r.id}`}
              >
                <span>
                  <code>{r.id.slice(0, 8)}</code> ·{" "}
                  {new Date(r.created_at).toLocaleString()}
                </span>
                <span>
                  P {pct(r.precision)} · R {pct(r.recall)} · F1 {pct(r.f1)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
